import React, { useEffect, useState } from "react";
import { Box, Chip, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from "../ui";
import SnpBaseLayout from "../global/SnpBaseLayout";
import SnpButton from "../global/SnpButton";
import SnpSwitch from "../global/SnpSwitch";
import useUiController from "../global/hooks/useUiController";
import { getSettingFromIndexedDB, saveSettingToIndexedDB } from "../global/indexedDB";
import { DEFAULT_VOICE_SETTINGS, normalizeVoiceSettings, VOICE_COMMAND_HELP, VOICE_COMMAND_KEYS, VOICE_SETTINGS_KEY } from "./voiceSettings";

export default function A2zVoiceSettings() {
  const { loader, setLoader, snackbarConfig, showSnackbar, closeSnackbar, dialogConfig } = useUiController();
  const [settings, setSettings] = useState(DEFAULT_VOICE_SETTINGS);
  const [newSynonyms, setNewSynonyms] = useState({});

  useEffect(() => {
    const loadSettings = async () => {
      setLoader(true);
      try {
        setSettings(normalizeVoiceSettings(await getSettingFromIndexedDB(VOICE_SETTINGS_KEY)));
      } catch (error) {
        console.error("Unable to load voice settings:", error);
        showSnackbar("Could not load saved voice settings.", "#b71c1c");
      } finally {
        setLoader(false);
      }
    };
    loadSettings();
  }, [setLoader, showSnackbar]);

  const setValue = (name, value) => setSettings((current) => ({ ...current, [name]: value }));

  const addSynonym = (command) => {
    const synonym = (newSynonyms[command] || "").trim();
    if (!synonym) return;

    const existingCommand = Object.entries(settings.commands || {}).find(([, synonyms]) =>
      synonyms.some((item) => item.toLowerCase() === synonym.toLowerCase())
    )?.[0];

    if (existingCommand) {
      showSnackbar(`"${synonym}" is already used by ${existingCommand}.`, "#b71c1c");
      return;
    }

    setSettings((current) => {
      const existing = current.commands?.[command] || [];
      return { ...current, commands: { ...current.commands, [command]: [...existing, synonym] } };
    });
    setNewSynonyms((current) => ({ ...current, [command]: "" }));
  };

  const deleteSynonym = (command, synonym) => {
    setSettings((current) => ({
      ...current,
      commands: {
        ...current.commands,
        [command]: (current.commands?.[command] || []).filter((item) => item.toLowerCase() !== synonym.toLowerCase()),
      },
    }));
  };

  const saveSettings = async () => {
    const nextSettings = normalizeVoiceSettings(settings);
    setLoader(true);
    try {
      await saveSettingToIndexedDB(VOICE_SETTINGS_KEY, nextSettings);
      setSettings(nextSettings);
      window.dispatchEvent(new CustomEvent("a2z-voice-settings-updated", { detail: nextSettings }));
      showSnackbar("Voice settings saved.", "#2e7d32");
    } catch (error) {
      console.error("Unable to save voice settings:", error);
      showSnackbar("Could not save voice settings.", "#b71c1c");
    } finally {
      setLoader(false);
    }
  };

  return (
    <SnpBaseLayout loader={loader} snackbarConfig={{ ...snackbarConfig, onClose: closeSnackbar }} dialogConfig={dialogConfig}>
      <Box sx={{ p: { xs: 1, sm: 3 } }}>
        <Typography variant="h5" sx={{ mb: 2 }}>A2Z Voice Settings</Typography>
        <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1050 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <SnpSwitch leftLabel="Disable voice commands" rightLabel="Enable voice commands" checked={settings.enabled} onChange={(event) => setValue("enabled", event.target.checked)} color="primary" />
            </Grid>
            {[
              ["listenDurationMs", "Listen duration (ms)", 1000, "Default: 7000 ms"],
              ["speechThreshold", "Speech threshold", 0.001, "Default: 0.025"],
              ["speechStartDelay", "Speech start delay (ms)", 0, "Default: 120 ms"],
              ["speechEndDelay", "Speech end delay (ms)", 0, "Default: 700 ms"],
            ].map(([name, label, min, helperText]) => (
              <Grid item xs={12} sm={6} key={name}>
                <TextField fullWidth type="number" variant="filled" label={label} helperText={helperText} inputProps={{ min, step: name === "speechThreshold" ? 0.001 : 1 }} value={settings[name]} onChange={(event) => setValue(name, Number(event.target.value))} />
              </Grid>
            ))}
            <Grid item xs={12}>
              <Typography variant="h6">Commands and synonyms</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Separate synonyms with commas. The first word of a spoken command determines its action.</Typography>
              <TableContainer sx={{ overflowX: "auto" }}>
              <Table size="small" sx={{ minWidth: 760 }}>
                <TableHead><TableRow><TableCell>Command</TableCell><TableCell>Synonyms</TableCell><TableCell>Example</TableCell><TableCell>What it does</TableCell></TableRow></TableHead>
                <TableBody>
                  {VOICE_COMMAND_KEYS.map((command) => (
                    <TableRow key={command}>
                      <TableCell sx={{ textTransform: "capitalize", fontWeight: "bold", whiteSpace: "nowrap" }}>{command}</TableCell>
                      <TableCell sx={{ minWidth: 300 }}>
                        <TextField fullWidth size="small" placeholder="Type a synonym and press Enter" helperText="Press Enter to add. A synonym can belong to only one command." value={newSynonyms[command] || ""} onChange={(event) => setNewSynonyms((current) => ({ ...current, [command]: event.target.value }))} onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); addSynonym(command); } }} />
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
                          {(settings.commands?.[command] || []).filter((synonym) => synonym.toLowerCase() !== command).map((synonym) => <Chip key={synonym} label={synonym} size="small" onDelete={() => deleteSynonym(command, synonym)} />)}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "normal", overflowWrap: "anywhere" }}>{VOICE_COMMAND_HELP[command].map((help) => <Box key={help.example}>{help.example}</Box>)}</TableCell>
                      <TableCell sx={{ whiteSpace: "normal", overflowWrap: "anywhere", wordBreak: "break-word", minWidth: 220 }}>{VOICE_COMMAND_HELP[command].map((help) => <Box key={help.example}>{help.action}</Box>)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </TableContainer>
            </Grid>
            <Grid item xs={12}><SnpButton variant="contained" onClick={saveSettings}>Save Voice Settings</SnpButton></Grid>
          </Grid>
        </Paper>
      </Box>
    </SnpBaseLayout>
  );
}
